import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Network } from "lucide-react";

export default function Estructuras() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: "", tipo: "vicerrectoria", descripcion: "" });

  const labels = {
    vicerrectoria: t("estructuras.tipo.vicerrectoria"),
    centro: t("estructuras.tipo.centro"),
    instituto: t("estructuras.tipo.instituto"),
    direccion: t("estructuras.tipo.direccion"),
    otro: t("estructuras.tipo.otro"),
  };

  const load = () => {
    setLoading(true);
    api.get("/estructuras").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ nombre: "", tipo: "vicerrectoria", descripcion: "" });
    setShowForm(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ nombre: it.nombre, tipo: it.tipo, descripcion: it.descripcion || "" });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/estructuras/${editing.id}`, form);
        toast.success(t("est_x.toast_updated"));
      } else {
        await api.post("/estructuras", form);
        toast.success(t("est_x.toast_created"));
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toast.error_generic"));
    }
  };

  const remove = async (it) => {
    if (!window.confirm(t("est_x.confirm_delete", { name: it.nombre }))) return;
    try {
      await api.delete(`/estructuras/${it.id}`);
      toast.success(t("est_x.toast_deleted"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toast.error_generic"));
    }
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
          <div>
            <div className="section-marker inline-flex items-center gap-2">
              <Network size={12} strokeWidth={2} /> {t("estructuras.kicker")}
            </div>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter mt-2">
              {t("estructuras.page_title")}<span className="serif-em text-zinc-900">.</span>
            </h1>
            <p className="mt-3 text-zinc-600 max-w-2xl">{t("estructuras.desc")}</p>
          </div>
          <button
            onClick={openCreate}
            className="bg-zinc-950 text-white px-5 py-3 font-medium hover:bg-zinc-800 inline-flex items-center justify-center gap-2 w-full sm:w-auto"
            data-testid="btn-new-estructura"
          >
            <Plus size={18} strokeWidth={1.5} /> {t("estructuras.btn_new")}
          </button>
        </div>

        {loading ? (
          <div className="text-zinc-500 py-8">{t("est_x.loading")}</div>
        ) : (
          <div className="border-t-2 border-zinc-950 overflow-x-auto">
            <table className="w-full min-w-[640px]" data-testid="estructuras-table">
              <thead>
                <tr>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("estructuras.col_nombre")}</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("estructuras.col_tipo")}</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("estructuras.col_desc")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={4} className="py-16 text-center text-zinc-500">
                    {t("estructuras.empty")}
                  </td></tr>
                )}
                {items.map((e) => (
                  <tr key={e.id} className="border-t border-zinc-100" data-testid={`estr-row-${e.id}`}>
                    <td className="py-4 font-medium">{e.nombre}</td>
                    <td className="py-4">
                      <span className="inline-block px-2 py-0.5 text-xs border border-zinc-300 bg-zinc-50">
                        {labels[e.tipo] || e.tipo}
                      </span>
                    </td>
                    <td className="py-4 text-zinc-600 text-sm">{e.descripcion || "—"}</td>
                    <td className="py-4 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(e)} className="text-zinc-400 hover:text-zinc-950 p-1 mr-2" aria-label={t("common.edit")} data-testid={`estr-edit-${e.id}`}>
                        <Pencil size={16} strokeWidth={1.5} />
                      </button>
                      <button onClick={() => remove(e)} className="text-zinc-400 hover:text-red-700 p-1" aria-label={t("common.delete")} data-testid={`estr-delete-${e.id}`}>
                        <Trash2 size={16} strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-lg p-6 sm:p-8 border border-zinc-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="estr-modal">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="section-marker">{editing ? t("estructuras.form_edit") : t("estructuras.form_create")}</div>
                <h2 className="text-2xl font-medium tracking-tight mt-1">{editing ? editing.nombre : t("estructuras.form_new_title")}</h2>
              </div>
              <button onClick={() => setShowForm(false)} className="text-zinc-400 hover:text-zinc-950 p-1">
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("estructuras.field_nombre")}</label>
                <input required value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} className="w-full border border-zinc-300 px-4 py-3" data-testid="estr-nombre" autoFocus />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("estructuras.field_tipo")}</label>
                <select value={form.tipo} onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))} className="w-full border border-zinc-300 px-4 py-3 bg-white" data-testid="estr-tipo">
                  {Object.entries(labels).map(([k, l]) => (
                    <option key={k} value={k}>{l}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("estructuras.field_desc")}</label>
                <textarea rows={3} value={form.descripcion} onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))} className="w-full border border-zinc-300 px-4 py-3" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="border border-zinc-300 px-6 py-3 font-medium hover:bg-zinc-50">{t("common.cancel")}</button>
                <button type="submit" className="flex-1 bg-zinc-950 text-white px-6 py-3 font-medium hover:bg-zinc-800" data-testid="estr-submit">
                  {editing ? t("est_x.save") : t("est_x.create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
